import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_MODIFIERS, DEFAULT_PLANT_DENSITY, getSuggestedServices,
  DEFAULT_SERVICES_BY_TYPE,
} from '../../SiteTakeoff/siteTakeoffConstants';

/**
 * Loads takeoffs for a jobsite and manages the currently-active takeoff's areas.
 * Returns areas + CRUD callbacks + takeoff list + active takeoff metadata.
 *
 * Fetches admin-configured service defaults per entity and uses those when
 * assigning services to new areas. Falls back to hardcoded defaults if none configured.
 */
export default function useAreas(url, jobsite) {
  const [areas, setAreas] = useState([]);
  const [takeoffs, setTakeoffs] = useState([]);
  const [takeoff, setTakeoff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const initialLoadRef = useRef(false);
  const customDefaultsRef = useRef(null); // admin-configured defaults

  // Fetch admin-configured service defaults
  useEffect(() => {
    if (!url) return;
    axios.get(`${url}/api/customer/area-type-defaults`)
      .then(resp => {
        const d = resp.data?.defaults;
        customDefaultsRef.current = (d && Object.keys(d).length > 0) ? d : null;
      })
      .catch(() => { customDefaultsRef.current = null; });
  }, [url]);

  // Get suggested services using admin overrides if available
  const getServices = useCallback((areaType) => {
    if (customDefaultsRef.current && customDefaultsRef.current[areaType]) {
      return customDefaultsRef.current[areaType];
    }
    return getSuggestedServices(areaType);
  }, []);

  const normalizeAreas = (rawAreas) =>
    (rawAreas || [])
      .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0])
      .map(a => ({
        ...a,
        id: a.id || a._id,
        plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
        modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
        servicesRequired: a.servicesRequired?.length ? a.servicesRequired : getServices(a.areaType),
        notes: a.notes || '',
        isNew: false,
      }));

  const fetchTakeoffList = useCallback(async (jobsiteId) => {
    if (!url || !jobsiteId) return [];
    try {
      const resp = await axios.get(`${url}/api/customer/site-takeoffs?jobSiteId=${jobsiteId}`);
      const list = resp.data?.takeoffs || [];
      setTakeoffs(list);
      return list;
    } catch (err) {
      console.error('fetchTakeoffList error:', err);
      setTakeoffs([]);
      return [];
    }
  }, [url]);

  const loadTakeoffById = useCallback(async (takeoffId) => {
    if (!url || !takeoffId) return null;
    setLoading(true);
    initialLoadRef.current = true;
    try {
      const resp = await axios.get(`${url}/api/customer/site-takeoff/${takeoffId}`);
      const { success, takeoff: t } = resp.data || {};
      if (success && t) {
        setAreas(normalizeAreas(t.areas));
        setTakeoff({
          _id: t._id,
          name: t.name || 'Site Takeoff',
          overlays: t.overlays || [],
          placementQueue: t.placementQueue || [],
          productionRates: t.productionRates || {},
        });
        setDirty(false);
        return t;
      }
    } catch (err) {
      console.error('loadTakeoffById error:', err);
    } finally {
      setLoading(false);
      setTimeout(() => { initialLoadRef.current = false; }, 0);
    }
    return null;
  }, [url]);

  useEffect(() => {
    if (!url || !jobsite?._id) {
      setAreas([]);
      setTakeoff(null);
      setTakeoffs([]);
      setDirty(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const list = await fetchTakeoffList(jobsite._id);
      if (cancelled) return;
      if (list.length > 0) {
        await loadTakeoffById(list[0]._id);
      } else {
        setAreas([]);
        setTakeoff(null);
        setDirty(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url, jobsite, fetchTakeoffList, loadTakeoffById]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    if (areas.length === 0 && !takeoff?._id) return;
    setDirty(true);
  }, [areas, takeoff]);

  const addArea = useCallback((area) => {
    const newArea = {
      id: uuidv4(),
      isNew: true,
      plantDensity: { ...DEFAULT_PLANT_DENSITY },
      modifiers: { ...DEFAULT_MODIFIERS },
      servicesRequired: getServices(area.areaType),
      notes: '',
      ...area,
    };
    setAreas(prev => [...prev, newArea]);
    return newArea;
  }, []);

  const updateArea = useCallback((areaId, updates) => {
    setAreas(prev => prev.map(a => {
      if ((a._id || a.id) !== areaId) return a;
      const merged = { ...a };
      Object.entries(updates).forEach(([k, v]) => {
        if (k === 'plantDensity' && typeof v === 'object') {
          merged.plantDensity = { ...(a.plantDensity || DEFAULT_PLANT_DENSITY), ...v };
        } else if (k === 'modifiers' && typeof v === 'object') {
          merged.modifiers = { ...(a.modifiers || DEFAULT_MODIFIERS), ...v };
        } else if (k === 'treeDetails' && typeof v === 'object') {
          merged.treeDetails = { ...(a.treeDetails || {}), ...v };
        } else {
          merged[k] = v;
        }
      });
      return merged;
    }));
  }, []);

  // Delete an area — cascade-delete child exclusions too
  const deleteArea = useCallback((areaId) => {
    setAreas(prev => prev.filter(a => {
      const id = a._id || a.id;
      if (id === areaId) return false;
      // Also remove child exclusions of the deleted area
      const pid = a.parentAreaId;
      if (pid && (pid === areaId || pid.toString?.() === areaId)) return false;
      return true;
    }));
  }, []);

  const duplicateArea = useCallback((areaId) => {
    setAreas(prev => {
      const source = prev.find(a => (a._id || a.id) === areaId);
      if (!source) return prev;
      const copy = {
        ...source,
        _id: undefined,
        id: uuidv4(),
        isNew: true,
        name: `${source.name || 'Area'} (copy)`,
      };
      return [...prev, copy];
    });
  }, []);

  // saveTakeoff(takeoffName, extras) where extras = { overlays?, placementQueue? }
  // Extras come from external hooks (useOverlays, etc) and override the values
  // cached on the takeoff state — so the latest in-memory geometry wins.
  const saveTakeoff = useCallback(async (takeoffName, extras = {}) => {
    if (!url || !jobsite?._id) return null;
    setSaving(true);
    try {
      const areasToSave = areas.map(a => ({
        _id: a._id || undefined,
        clientId: a.id || undefined,
        name: a.name || '',
        areaType: a.areaType,
        geometryType: a.geometryType || 'polygon',
        paths: a.paths,
        area: a.area,
        areaAcres: a.areaAcres,
        perimeter: a.perimeter,
        length: a.length,
        width: a.width,
        radiusX: a.radiusX,
        radiusY: a.radiusY,
        plantDensity: a.plantDensity,
        modifiers: a.modifiers,
        servicesRequired: a.servicesRequired || [],
        notes: a.notes || '',
        treeDetails: a.treeDetails,
        groupId: a.groupId,
        groupName: a.groupName,
        fillColor: a.fillColor,
        strokeColor: a.strokeColor,
        parentAreaId: a.parentAreaId || null,
        exclusionType: a.exclusionType || null,
      }));

      const resolvedOverlays = Array.isArray(extras?.overlays)
        ? extras.overlays
        : (takeoff?.overlays || []);
      const resolvedQueue = Array.isArray(extras?.placementQueue)
        ? extras.placementQueue
        : (takeoff?.placementQueue || []);

      const resp = await axios.post(`${url}/api/hmi/genericPostRequest?type=SAVE_SITE_TAKEOFF`, {
        takeoffId: takeoff?._id || undefined,
        takeoffName: takeoffName || takeoff?.name || 'Site Takeoff',
        jobSite: { _id: jobsite._id },
        areas: areasToSave,
        overlays: resolvedOverlays,
        placementQueue: resolvedQueue,
        productionRates: takeoff?.productionRates || undefined,
      });

      if (resp.data?.success && resp.data?.takeoff?._id) {
        const savedId = resp.data.takeoff._id;
        const savedName = resp.data.takeoff.name || takeoffName;
        setTakeoff(prev => ({
          ...(prev || {}),
          _id: savedId,
          name: savedName,
          overlays: resolvedOverlays,
          placementQueue: resolvedQueue,
        }));
        const serverIds = resp.data.takeoff.areaIds || [];
        if (serverIds.length === areasToSave.length) {
          setAreas(prev => prev.map((a, i) => ({
            ...a,
            _id: serverIds[i],
            isNew: false,
          })));
        }
        setDirty(false);
        await fetchTakeoffList(jobsite._id);
        return resp.data.takeoff;
      }
    } catch (err) {
      console.error('saveTakeoff error:', err);
    } finally {
      setSaving(false);
    }
    return null;
  }, [url, jobsite, areas, takeoff, fetchTakeoffList]);

  const newTakeoff = useCallback(() => {
    initialLoadRef.current = true;
    setAreas([]);
    setTakeoff({
      _id: null,
      name: 'New Site Takeoff',
      overlays: [],
      placementQueue: [],
      productionRates: {},
    });
    setDirty(false);
    setTimeout(() => { initialLoadRef.current = false; }, 0);
  }, []);

  const switchTakeoff = useCallback(async (takeoffId) => {
    if (!takeoffId || takeoffId === takeoff?._id) return;
    await loadTakeoffById(takeoffId);
  }, [takeoff, loadTakeoffById]);

  const deleteTakeoff = useCallback(async (takeoffId) => {
    if (!url || !takeoffId) return false;
    try {
      await axios.delete(`${url}/api/customer/site-takeoff/${takeoffId}`);
      const list = await fetchTakeoffList(jobsite?._id);
      if (takeoff?._id === takeoffId) {
        if (list.length > 0) {
          await loadTakeoffById(list[0]._id);
        } else {
          setAreas([]);
          setTakeoff(null);
          setDirty(false);
        }
      }
      return true;
    } catch (err) {
      console.error('deleteTakeoff error:', err);
      return false;
    }
  }, [url, jobsite, takeoff, fetchTakeoffList, loadTakeoffById]);

  const renameTakeoff = useCallback((newName) => {
    setTakeoff(prev => prev ? { ...prev, name: newName } : prev);
    setDirty(true);
  }, []);

  // Update production rate overrides on the active takeoff. Marks dirty
  // so the user has to save to persist them.
  const updateProductionRates = useCallback((newRates) => {
    setTakeoff(prev => prev ? { ...prev, productionRates: newRates || {} } : prev);
    setDirty(true);
  }, []);

  // Import legacy JobSite.areas as a fresh new takeoff (creates new Area records)
  const importLegacyAreas = useCallback(async (legacyAreas, name = 'Imported Areas') => {
    if (!url || !jobsite?._id || !legacyAreas?.length) return null;
    setSaving(true);
    try {
      // Strip _id so the server creates new Area records instead of mutating the originals
      const areasToSave = legacyAreas
        .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0])
        .map(a => ({
          name: a.name || '',
          areaType: a.areaType,
          geometryType: a.geometryType || 'polygon',
          paths: a.paths,
          area: a.area,
          areaAcres: a.areaAcres,
          perimeter: a.perimeter,
          length: a.length || '0',
          width: a.width || 0,
          radiusX: a.radiusX || 0,
          radiusY: a.radiusY || 0,
          plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
          modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
          servicesRequired: a.servicesRequired?.length
            ? a.servicesRequired
            : getServices(a.areaType),
          notes: a.notes || '',
          treeDetails: a.treeDetails,
          groupId: a.groupId,
          groupName: a.groupName,
          fillColor: a.fillColor,
          strokeColor: a.strokeColor || '#000000',
        }));

      if (areasToSave.length === 0) return null;

      const resp = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=SAVE_SITE_TAKEOFF`,
        {
          takeoffName: name,
          jobSite: { _id: jobsite._id },
          areas: areasToSave,
        }
      );

      if (resp.data?.success && resp.data?.takeoff?._id) {
        // Reload list + switch to the new takeoff
        await fetchTakeoffList(jobsite._id);
        await loadTakeoffById(resp.data.takeoff._id);
        return resp.data.takeoff;
      }
    } catch (err) {
      console.error('importLegacyAreas error:', err);
    } finally {
      setSaving(false);
    }
    return null;
  }, [url, jobsite, fetchTakeoffList, loadTakeoffById]);

  return {
    areas,
    takeoffs,
    takeoff,
    loading,
    saving,
    dirty,
    addArea,
    updateArea,
    deleteArea,
    duplicateArea,
    saveTakeoff,
    newTakeoff,
    switchTakeoff,
    deleteTakeoff,
    renameTakeoff,
    updateProductionRates,
    importLegacyAreas,
  };
}
